<script setup lang="ts">
import { Ref } from "vue";

const props = defineProps({
  show: {
    type: Boolean || undefined,
    required: false,
    default: false
  },
  message: String,
  alertType: {
    type: String,
    default: "success"
  },
  alertId: {
    type: String,
    default: "alert"
  }
});

const emit = defineEmits(["reset"]);

const alertClass = computed(() => {
  switch (props.alertType) {
    case "success":
      return "alert-success";
    case "warning":
      return "alert-warning";
    case "danger":
      return "alert-danger";
    case "error":
      return "alert-danger";
    default:
      return "alert-primary";
  }
});

const alertVisible = ref(props.show);
const alertIsOnPage = ref(props.show);

const hideAlert = () => {
  alertVisible.value = false;
};

const showAlert = () => {
  alertIsOnPage.value = true;
  alertVisible.value = true;
};

watchEffect(() => {
  if (alertVisible.value) {
    resetVisibilityTimeout();
  }
});

watchEffect(() => {
  alertVisible.value = props.show;
});

const visibilityTimeout: Ref<number> = ref(-1);
const isOnPageTimeout: Ref<number> = ref(-1);

const resetVisibilityTimeout = () => {
  window.clearTimeout(visibilityTimeout.value);
  visibilityTimeout.value = window.setTimeout(() => {
    alertVisible.value = false;
  }, 5000);
};

const resetIsOnPageTimeout = () => {
  window.clearTimeout(isOnPageTimeout.value);
  isOnPageTimeout.value = window.setTimeout(() => {
    alertIsOnPage.value = false;
    emit("reset");
  }, 150);
};

watch(
  () => props.show,
  (newValue, oldValue) => {
    if (!oldValue && newValue) {
      showAlert();
    }

    if (oldValue && !newValue) {
      hideAlert();
    }

    // if both are equal we do nothing
  }
);

const visibilityClass = computed(() => (alertVisible.value ? "show" : "hide"));

watchEffect(() => {
  if (visibilityClass.value === "hide") {
    resetIsOnPageTimeout();
  }
});

watch(
  () => props.alertId,
  (newValue, oldValue) => {
    if (newValue !== oldValue) {
      resetVisibilityTimeout();
    }
  }
);
</script>

<template>
  <div v-if="alertIsOnPage" class="banner-alert-container">
    <div
      class="alert alert-dismissible fade banner-alert"
      :class="[alertClass, visibilityClass]"
      role="alert"
    >
      <span>
        {{ message }}
      </span>
      <button
        type="button"
        class="btn-close button-text no-box-shadow"
        aria-label="Close"
        @click="hideAlert"
      />
    </div>
  </div>
</template>

<style scoped>
.banner-alert-container {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 10;
  width: 100%;
}

.banner-alert {
  width: 90%;
  min-height: 3.5rem;
  margin: 1rem auto;
  font-size: 1rem;
  line-height: 1.2;
}

.fade {
  transition: opacity 0.15s linear;
}

.no-box-shadow {
  box-shadow: none !important;
}

@media screen and (max-width: 925px) {
  .banner-alert-container {
    top: auto;
    bottom: 0;
    width: 100%;
    z-index: 11;
  }
}
</style>
